import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import type { ParseKeys } from "i18next";

import type { ILocaleType } from "@configs";
import type {
  IAlertProps,
  IShowAlertParams,
} from "@components/molecules/alert";
import { ALERT_DEFAULT_PROPS } from "@components/molecules/alert/config";
import { IThemeModeEnum } from "@/theme/types";
import type { IBreadCrumbItem } from "@/stores/breadCrumbStore";

export type IPageModeType = "index" | "edit" | "view" | "create";
export type IPageType = "main" | "inner";

interface IUiState {
  locale: ILocaleType;
  changeLocale: (value: ILocaleType) => void;
  theme: IThemeModeEnum;
  setTheme: (value: IThemeModeEnum) => void;
  alertProps: IAlertProps;
  showAlert: (props: IAlertProps) => void;
  pageTitle: ParseKeys<"common">;
  breadCrumbs: IBreadCrumbItem[];
  pageMode: IPageModeType;
  pageType: IPageType;
  setPage: (
    request: Request,
    pageTitle: ParseKeys<"common">,
    breadCrumbs: IBreadCrumbItem[],
    pageMode: IPageModeType,
    pageType?: IPageType,
  ) => void;
  /**
   * the path that has been set for pageTitle, pageMode and pageBreadcrumbs
   */
  locatedPath: string;
}

// partialized items are stored in localStorge
export const useUIStore = create<IUiState>()(
  subscribeWithSelector(
    persist(
      devtools(
        (set) => ({
          locale: import.meta.env.VITE_LANGUAGE || "fa",
          changeLocale: (value) => set({ locale: value }),
          theme: IThemeModeEnum.BONDI_LIGHT,
          setTheme: (value) => set({ theme: value }),
          alertProps: ALERT_DEFAULT_PROPS,
          showAlert: (value) => set({ alertProps: value }),
          pageTitle: "pageTitle.usersManagement",
          locatedPath: "",
          pageMode: "create",
          pageType: "inner",
          breadCrumbs: [],
          setPage(
            request,
            pageTitle,
            breadCrumbs,
            pageMode,
            pageType = "inner",
          ) {
            set({
              pageTitle,
              locatedPath: request.url,
              breadCrumbs,
              pageMode: pageMode === "index" ? "create" : pageMode,
              pageType,
            });
          },
        }),
        {
          enabled: !import.meta.env.PROD,
          name: "uiStore",
        },
      ),
      {
        name: "uiState",
        partialize: (state) => ({
          theme: state.theme,
          locale: state.locale,
        }),
      },
    ),
  ),
);

/**
 * Shows a generic alert dialog.
 * @param {IShowAlertParams} alertProps - The properties of the alert.
 */
export const showAlert = (alertProps: IShowAlertParams) => {
  useUIStore.setState(() => ({
    alertProps: {
      ...alertProps,
      open: true,
    },
  }));
};

/**
 * Closes the currently open alert dialog.
 */
export const closeAlert = () => {
  useUIStore.setState(() => ({ alertProps: ALERT_DEFAULT_PROPS }));
};
